import { isCopyPlainTextStripEnabled } from '@/features/flags/copy-plain-text-strip';
import { PARAGRAPH_BLANK_LINE_MARKER, stripUnderlineHtmlPairs } from '@heidi/editor/core';
import {
  removeAnnotationFromMarkdown,
  UNDERLINE_CLOSE_TAG_SOURCE,
  UNDERLINE_PAIR_OPEN_TAG_SOURCE,
} from '../htmlToMarkdown';

/**
 * Pre-funnel markdown for producers that historically ran
 * `removeAnnotationFromMarkdown`. With the strip rollout on they hand the
 * funnel raw markdown instead: its looser regexes mangle snake_case
…
 */
export const resolvePlainCopyMarkdown = (markdown: string): string =>
  isCopyPlainTextStripEnabled() ? markdown : removeAnnotationFromMarkdown(markdown);

type ProtectedMarkdown = {
  markdown: string;
  restore: (value: string) => string;
};

const findProtectionDelimiter = (markdown: string): string => {
  const inputCharacters = new Set(Array.from(markdown));
  for (let codePoint = 0xe000; codePoint <= 0xf8ff; codePoint += 1) {
    const candidate = String.fromCodePoint(codePoint);
    if (!inputCharacters.has(candidate)) return candidate;
  }

  const fallbackCharacter = String.fromCodePoint(0xe000);
  const fallbackLength =
    Array.from(markdown).filter((character) => character === fallbackCharacter).length +
    1;
  return fallbackCharacter.repeat(fallbackLength);
};

const protectLiteralMarkdown = (
  markdown: string,
  { keepHtmlCodeWrappers = false }: { keepHtmlCodeWrappers?: boolean } = {}
): ProtectedMarkdown => {
  const protectedValues: string[] = [];
  const delimiter = findProtectionDelimiter(markdown);
  const tokenFor = (index: number): string =>
    `${delimiter}${index.toString(36)}${delimiter}`;
  const protect = (value: string): string => {
    const token = tokenFor(protectedValues.length);
    protectedValues.push(value);
    return token;
  };

  const lines = markdown.split('\n');
  const withoutFencedCode: string[] = [];
  const visualWidth = (value: string): number =>
    Array.from(value).reduce(
      (width, character) => width + (character === '\t' ? 4 : 1),
      0
    );
  const indentationWidth = (line: string): number =>
    visualWidth(line.match(/^[\t ]*/)?.[0] ?? '');
  const listMarkerWidth = (line: string): number | null => {
    const marker = /^(?:[-+*]|\d{1,9}[.)])[\t ]+/.exec(line.trimStart())?.[0];
    return marker == null ? null : visualWidth(marker);
  };
  const isListRelativeIndentation = (index: number): boolean => {
    const line = lines[index] ?? '';
    const currentIndentation = indentationWidth(line);
    if (currentIndentation < 4) return false;
    const isNestedMarkerLine = listMarkerWidth(line) != null;

    for (let previousIndex = index - 1; previousIndex >= 0; previousIndex -= 1) {
      const previousLine = lines[previousIndex] ?? '';
      // A blank line separates a list item from its continuation paragraph
      // without ending the list, so keep scanning for the parent marker.
      if (previousLine.trim() === '') continue;

      const previousIndentation = indentationWidth(previousLine);
      const markerWidth = listMarkerWidth(previousLine);
      if (markerWidth != null) {
        // An item's content column is its marker indent plus the width that
        // marker actually printed — two under `- `, three under `1. `, four
        // under `10. `. A fixed four mis-reads every odd column. A nested list
        // is the exception: it keeps the serializer's stock two-space indent
        // rather than reaching the column, so it only has to sit deeper.
        // Four past the column is indented code inside the item either way.
        const contentColumn = previousIndentation + markerWidth;
        const lowestOwnColumn = isNestedMarkerLine
          ? previousIndentation + 1
          : contentColumn;
        if (
          currentIndentation >= lowestOwnColumn &&
          currentIndentation < contentColumn + 4
        )
          return true;
      }
      // A shallower line that cannot host this one means the list ended above.
      if (previousIndentation < currentIndentation) return false;
    }
    return false;
  };
  let lineIndex = 0;

  while (lineIndex < lines.length) {
    const line = lines[lineIndex] ?? '';
    if (/^(?: {4}|\t)/.test(line) && !isListRelativeIndentation(lineIndex)) {
      const block = [line];
      lineIndex += 1;
      while (lineIndex < lines.length) {
        const blockLine = lines[lineIndex] ?? '';
        if (blockLine !== '' && !/^(?: {4}|\t)/.test(blockLine)) break;
        block.push(blockLine);
        lineIndex += 1;
      }
      // Trailing blank lines are not part of the code block; leave them in
      // the stream so a following block stays `\n\n`-delimited.
      while (block.length > 1 && block[block.length - 1] === '') {
        block.pop();
        lineIndex -= 1;
      }
      withoutFencedCode.push(protect(block.join('\n')));
      continue;
    }

    const openingFence = line.match(/^ {0,3}(`{3,}|~{3,})/);
    if (openingFence == null) {
      withoutFencedCode.push(line);
      lineIndex += 1;
      continue;
    }

    const fence = openingFence[1] ?? '';
    const closingFence = new RegExp(`^ {0,3}${fence[0] ?? '`'}{${fence.length},}\\s*$`);
    const block = [line];
    lineIndex += 1;
    while (lineIndex < lines.length) {
      const blockLine = lines[lineIndex] ?? '';
      block.push(blockLine);
      lineIndex += 1;
      if (closingFence.test(blockLine)) break;
    }
    withoutFencedCode.push(protect(block.join('\n')));
  }

  const protectHtmlCode = (value: string, content: string): string =>
    protect(keepHtmlCodeWrappers ? value : content);
  const htmlCodeProtected = withoutFencedCode
    .join('\n')
    .replace(
      /<pre\b[^>]*>\s*<code\b[^>]*>([\s\S]*?)<\/code>\s*<\/pre>/gi,
      (value, content: string) => protectHtmlCode(value, content)
    )
    .replace(/<code\b[^>]*>([\s\S]*?)<\/code>/gi, (value, content: string) =>
      protectHtmlCode(value, content)
    )
    .replace(/<pre\b[^>]*>([\s\S]*?)<\/pre>/gi, (value, content: string) =>
      protectHtmlCode(value, content)
    );
  const escapedMarkersProtected = htmlCodeProtected.replace(/\\[`*_]/g, (value) =>
    protect(value)
  );
  const literalsProtected = escapedMarkersProtected.replace(
    /(`+)([\s\S]*?)\1/g,
    (value) => protect(value)
  );

  return {
    markdown: literalsProtected,
    restore: (value: string) =>
      protectedValues.reduceRight(
        (restored, protectedValue, index) =>
          restored.split(tokenFor(index)).join(protectedValue.replace(/\n/g, '\r\n')),
        value
      ),
  };
};

const stripVisualEmphasis = (markdown: string): string => {
  let stripped = markdown.replace(/\*\*\*([^\s*\n](?:[^*\n]*?[^\s*\n])?)\*\*\*/g, '$1');
  stripped = stripped.replace(
    /(^|[^\w])___([^\s_\n](?:[^_\n]*?[^\s_\n])?)___(?!\w)/g,
    '$1$2'
  );
  stripped = stripped.replace(/\*\*([^\s*\n](?:[^*\n]*?[^\s*\n])?)\*\*/g, '$1');
  stripped = stripped.replace(
    /(^|[^\w])__([^\s_\n](?:[^_\n]*?[^\s_\n])?)__(?!\w)/g,
    '$1$2'
  );
  stripped = stripped.replace(
    /(^|[^\w])\*([^\s*\n](?:[^*\n]*?[^\s*\n])?)\*(?!\*)/g,
    '$1$2'
  );
  stripped = stripped.replace(
    /(^|[^\w])_([^\s_\n](?:[^_\n]*?[^\s_\n])?)_(?!\w)/g,
    '$1$2'
  );
  // The pair-only, linear underline strip shared with
  // `removeAnnotationFromMarkdown`: only a tag it can see closed is unwrapped,
  // and a nested pair's inner closer consumes the outer opener, leaving the
  // inner opener as residue — never guessed at, so orphaned openers and
  // self-closing `<u/>` shapes stay exactly as authored.
  return stripUnderlineHtmlPairs(stripped);
};

/**
 * Drop ATX heading markers, keeping the heading text: the opening `#{1,6}` run
 * (its space is what makes it a heading — a bare `#5` is content) and any
 * closed-form trailing run. Runs on protected markdown, so `#` inside code
 * stays literal. The tail comes off with index scans — a lazy body plus an
 * optional tail in one regex backtracks quadratically.
 */
const isHeadingTailWhitespace = (character: string | undefined): boolean =>
  character === ' ' || character === '\t';
const stripHeadingMarkers = (markdown: string): string =>
  markdown.replace(
    /^( {0,3})#{1,6}[ \t]+(.*)$/gm,
    (_line, indent: string, text: string) => {
      let end = text.length;
      while (isHeadingTailWhitespace(text[end - 1])) end -= 1;
      let beforeHashes = end;
      while (text[beforeHashes - 1] === '#') beforeHashes -= 1;
      // A closing run only counts when whitespace separates it from the text —
      // `# Plan#` keeps its hash, and a hashes-only body (`# ###`) is content.
      if (beforeHashes < end && isHeadingTailWhitespace(text[beforeHashes - 1])) {
        end = beforeHashes;
        while (isHeadingTailWhitespace(text[end - 1])) end -= 1;
      }
      return indent + text.slice(0, end);
    }
  );
const splitPlainTextTableRow = (row: string): string[] => {
  const cells = [''];
  let precedingBackslashes = 0;

  for (const character of row) {
    const cellIndex = cells.length - 1;
    if (character === '|') {
      const cell = cells[cellIndex] ?? '';
      const literalBackslashes = '\\'.repeat(Math.floor(precedingBackslashes / 2));
      if (precedingBackslashes > 0) {
        cells[cellIndex] = `${cell.slice(0, -precedingBackslashes)}${literalBackslashes}`;
      }

      if (precedingBackslashes % 2 === 1) {
        cells[cellIndex] += '|';
      } else {
        cells.push('');
      }
      precedingBackslashes = 0;
      continue;
    }

    cells[cellIndex] += character;
    precedingBackslashes = character === '\\' ? precedingBackslashes + 1 : 0;
  }

  if (cells[0]?.trim() === '') cells.shift();
  if (cells[cells.length - 1]?.trim() === '') cells.pop();
  return cells.map((cell) => cell.trim().replace(/<br\s*\/?>/gi, '\n'));
};

const tableRowCells = (line: string): string[] | null => {
  const trimmed = line.trim();
  if (!trimmed.startsWith('|')) return null;
  const cells = splitPlainTextTableRow(trimmed);
  return cells.length > 0 ? cells : null;
};

const isTableSeparatorRow = (line: string, columnCount: number): boolean => {
  const cells = tableRowCells(line);
  return cells?.length === columnCount && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
};

/**
 * Flatten only structurally valid GFM table blocks: a leading-pipe header
 * immediately followed by a same-width delimiter row whose cells contain at
 * least three dashes (with optional alignment colons). Standalone clinical
 * prose such as `|ΔBP|` must remain literal.
 */
const flattenPlainTextTableRows = (markdown: string): string => {
  const lines = markdown.split('\n');
  const flattened: string[] = [];
  let index = 0;

  while (index < lines.length) {
    const header = lines[index];
    const separator = lines[index + 1];
    const headerCells = header == null ? null : tableRowCells(header);

    if (
      header != null &&
      separator != null &&
      headerCells != null &&
      isTableSeparatorRow(separator, headerCells.length)
    ) {
      flattened.push(headerCells.join('\t'));
      index += 2;

      while (index < lines.length) {
        const row = lines[index];
        const cells = row == null ? null : tableRowCells(row);
        if (cells == null || cells.length !== headerCells.length) break;
        flattened.push(cells.join('\t'));
        index += 1;
      }
      continue;
    }

    if (header != null) flattened.push(header);
    index += 1;
  }

  return flattened.join('\n');
};

/**
 * Reduce markdown to what a plain-text EHR field visually shows. Normalize
 * input line endings first so blank-line collapsing behaves identically for
 * LF, CRLF, classic CR, and mixed input, then emit Windows-native CRLF.
 */
export const stripMarkdownForPlainText = (markdown: string): string => {
  const normalized = markdown.replace(/\r\n|\r/g, '\n');
  const protectedMarkdown = protectLiteralMarkdown(normalized);
  const withoutHeadings = stripHeadingMarkers(protectedMarkdown.markdown);
  const withoutAnnotations = stripVisualEmphasis(withoutHeadings);
  const tabDelimited = flattenPlainTextTableRows(withoutAnnotations);
  const collapsed = tabDelimited.replace(/\n{3,}/g, '\n\n');
  return protectedMarkdown.restore(collapsed.replace(/\n/g, '\r\n'));
};

const BLOCK_SEPARATOR = '\n\n';

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * The glued form: an item's first paragraph renders on the same line as its
 * container prefix (`- &nbsp;`, `> &nbsp;`, `> - &nbsp;`), so no block is ever
 * only the marker. Drop just the marker — a prefix means the item still
 * exists, it is only its text that was blank — and leave the separators
 * around the line alone.
 *
 * Runs after the block pass, which has already claimed every marker that stood
 * alone as a block, indented ones included. The bare-indent alternative here
 * therefore only catches a marker sharing its block with a sibling line.
 */
const PREFIXED_BLANK_LINE_MARKER = new RegExp(
  `^((?:>[ \\t]?)+[ \\t]*(?:(?:[-*+]|\\d{1,9}[.)])[ \\t]+)?|[ \\t]*(?:[-*+]|\\d{1,9}[.)])[ \\t]+|[ \\t]+)${escapeRegExp(
    PARAGRAPH_BLANK_LINE_MARKER
  )}[ \\t]*$`,
  'gm'
);

/**
 * A whole block that is nothing but the marker, give or take the indentation
 * of a list item's content column. Leading spaces/tabs are tolerated so an
 * indented continuation paragraph gets the same N + 1 arithmetic as the
 * top-level form; trailing whitespace is not, because the serializer never
 * pads the marker and a padded line is therefore someone's own content.
 *
 * Only spaces and tabs are stripped — a leading `\n` means the block came
 * from a longer newline run and is not a serialized blank line at all.
 */
const isBlankLineMarkerBlock = (block: string): boolean =>
  block.replace(/^[ \t]+/, '') === PARAGRAPH_BLANK_LINE_MARKER;

/**
 * Rejoin `\n\n`-delimited blocks, dropping marker-only ones while keeping the
 * blank line each stood for: N empty paragraphs between two blocks become
 * N + 1 blank lines.
 */
const rejoinWithoutMarkerBlocks = (text: string): string => {
  let output = '';
  let hasContent = false;
  let blanks = 0;

  for (const block of text.split(BLOCK_SEPARATOR)) {
    if (isBlankLineMarkerBlock(block)) {
      blanks += 1;
      continue;
    }
    output += '\n'.repeat(hasContent ? blanks + 2 : blanks) + block;
    hasContent = true;
    blanks = 0;
  }

  if (blanks === 0) return output;
  // A document that is nothing but markers has no separator to absorb, so the
  // first one contributes no newline of its own.
  return output + '\n'.repeat(hasContent ? blanks : Math.max(blanks - 1, 0));
};

/**
 * Presentation-side inverse of the markdown-native blank-line marker: an empty
 * paragraph persists as a literal `&nbsp;`, which a plain-text paste target
 * shows verbatim. Storage keeps emitting the marker — only the clipboard
 * `text/plain` flavor is rewritten here.
 *
 * Runs on `\n\n`-delimited blocks because every container in the
 * markdown-native kit joins sibling paragraphs with exactly that separator, so
 * a standalone empty paragraph always occupies a whole block and prose is
 * never rewritten. The N + 1 blank-line rule covers every marker that stands
 * alone as a block, top-level or indented to a list item's content column.
 * Only the glued form — the marker sharing a line with its container prefix —
 * is emptied in place, where keeping the surrounding separators is correct.
 *
 * MUST run after `stripMarkdownForPlainText`, which collapses `\n{3,}` to a
 * single blank line and would undo the blank lines restored here.
 * {@link resolveClipboardPlainText} owns that ordering for every producer.
 */
export const collapseParagraphBlankLineMarkers = (text: string): string => {
  if (!text.includes(PARAGRAPH_BLANK_LINE_MARKER)) return text;

  const isCrlf = text.includes('\r\n');
  // Unlike `stripMarkdownForPlainText`, which unwraps HTML code elements on
  // purpose, this pass must only touch markers — keep the wrappers.
  const protectedMarkdown = protectLiteralMarkdown(text.replace(/\r\n|\r/g, '\n'), {
    keepHtmlCodeWrappers: true,
  });
  // Block pass first: it owns the N + 1 arithmetic, and emptying a standalone
  // indented marker in place beforehand would leave a bare block behind and
  // spend one blank line too many.
  const withoutMarkerBlocks = rejoinWithoutMarkerBlocks(protectedMarkdown.markdown);
  const withoutPrefixedMarkers = withoutMarkerBlocks.replace(
    PREFIXED_BLANK_LINE_MARKER,
    (_match, prefix: string) => prefix.replace(/[ \t]+$/, '')
  );

  // `restore` always emits CRLF inside protected values, so restore against
  // CRLF text and convert back when the caller's input was LF.
  const restored = protectedMarkdown.restore(
    withoutPrefixedMarkers.replace(/\n/g, '\r\n')
  );
  return isCrlf ? restored : restored.replace(/\r\n/g, '\n');
};

/**
 * A marker carrying emphasis is the clinician's own text, not a serialized
 * blank line — `isBlankLineMarker` requires zero marks precisely so it stays
 * content. `stripVisualEmphasis` removes those marks, which would leave a bare
 * marker for the collapse below to delete, so stand the span on a placeholder
 * across both passes and restore the visible entity once they are done. The
 * underline alternative shares its attribute and closer shapes with
 * `stripVisualEmphasis`; case-sensitivity is deliberately narrower (`g`, not
 * `gi`) — an `i` flag would also case-fold the `&nbsp;` literal, and restore
 * rewrites the placeholder as canonical lowercase, so a clinician's literal
 * `&NBSP;` would come back mutated. An uppercase `<U>` wrap therefore stays
 * unprotected, as it always has been.
 */
const MARKED_BLANK_LINE_MARKER = new RegExp(
  `\\*{1,3}${escapeRegExp(PARAGRAPH_BLANK_LINE_MARKER)}\\*{1,3}` +
    `|_{1,3}${escapeRegExp(PARAGRAPH_BLANK_LINE_MARKER)}_{1,3}` +
    `|${UNDERLINE_PAIR_OPEN_TAG_SOURCE}[ \\t]*${escapeRegExp(PARAGRAPH_BLANK_LINE_MARKER)}[ \\t]*${UNDERLINE_CLOSE_TAG_SOURCE}`,
  'g'
);

const protectMarkedBlankLineMarkers = (markdown: string): ProtectedMarkdown => {
  if (!markdown.includes(PARAGRAPH_BLANK_LINE_MARKER)) {
    return { markdown, restore: (value) => value };
  }
  const placeholder = findProtectionDelimiter(markdown);
  return {
    markdown: markdown.replace(MARKED_BLANK_LINE_MARKER, placeholder),
    restore: (value) => value.split(placeholder).join(PARAGRAPH_BLANK_LINE_MARKER),
  };
};

/**
 * The shared funnel for producers' `text/plain` flavors (the vJSON note and
 * section copies still write directly).
 *
 * Applies the strip rollout to every copy — plain-only copies included, since
 * a plain-text paste target is exactly where leaked markers are most visible —
 * and then always runs {@link collapseParagraphBlankLineMarkers}. The collapse
 * is unconditional rather than opt-in because the invariant it enforces is
 * unconditional: no path may hand stored markdown out with the blank-line
 * marker still in it, and an opt-in leaks the entity again the first time a
 * producer forgets. It is a no-op on markdown that has no marker, which is
 * every legacy-editor copy.
 */
export const resolveClipboardPlainText = ({ markdown }: { markdown: string }): string => {
  if (!isCopyPlainTextStripEnabled()) {
    return collapseParagraphBlankLineMarkers(markdown);
  }
  const marked = protectMarkedBlankLineMarkers(markdown);
  return marked.restore(
    collapseParagraphBlankLineMarkers(stripMarkdownForPlainText(marked.markdown))
  );
};
